import { expectOk } from './http';
import { addDays, localDateTime } from './datetime';

async function fetchShifts(client, employeeId, weekStart, locationId) {
  const endDate = addDays(weekStart, 6);
  const params = { employeeId, startDate: weekStart, endDate };
  if (locationId) params.locationId = locationId;
  const response = await client.get('/shifts', { params });
  const scoped = await expectOk(response, 'GET /shifts');
  const allResponse = await client.get('/shifts', {
    params: { employeeId, startDate: weekStart, endDate },
  });
  const all = await expectOk(allResponse, 'GET /shifts (all locations)');
  const byId = new Map();
  for (const s of [...scoped, ...all]) byId.set(s.id, s);
  return [...byId.values()];
}

async function fetchAttendance(client, employeeId, weekStart, locationId, allLocations = false) {
  const endDate = addDays(weekStart, 6);
  const params = { employeeId, startDate: addDays(weekStart, -1), endDate: addDays(endDate, 1) };
  if (locationId && !allLocations) params.locationId = locationId;
  const response = await client.get('/attendance', { params });
  return expectOk(response, 'GET /attendance');
}

async function deleteShift(client, shiftId) {
  const response = await client.delete(`/shifts/${shiftId}`);
  if (response.status === 404) return;
  await expectOk(response, `DELETE /shifts/${shiftId}`);
}

async function deleteAttendance(client, attendanceId, hard = true) {
  const response = await client.delete(`/attendance/${attendanceId}`, {
    params: hard ? { isHardDelete: true } : undefined,
  });
  if (response.status === 404) return;
  await expectOk(response, `DELETE /attendance/${attendanceId}`);
}

async function getTimecard(client, employeeId, weekStart, locationId) {
  const params = {};
  if (locationId) params.locationId = locationId;
  const response = await client.get(`/timecards/employee/${employeeId}/week/${weekStart}`, { params });
  if (response.status === 404) return null;
  return expectOk(response, 'GET timecard');
}

async function unapproveTimecard(client, timecardId) {
  return client.post(`/timecards/${timecardId}/unapprove`);
}

async function cleanupWeek(client, employeeId, weekStart, locationId) {
  const existing = await getTimecard(client, employeeId, weekStart, locationId);
  if (existing?.isApproved && existing?.id) {
    const unapproveResponse = await unapproveTimecard(client, existing.id);
    if (unapproveResponse.status >= 400) {
      await expectOk(unapproveResponse, 'POST /timecards/unapprove before cleanup');
    }
  }

  const shifts = await fetchShifts(client, employeeId, weekStart, locationId);
  for (const shift of shifts) {
    await deleteShift(client, shift.id);
  }

  const attendance = await fetchAttendance(client, employeeId, weekStart, locationId, true);
  for (const att of attendance) {
    await deleteAttendance(client, att.id, true);
  }

  const body = { employeeId, weekStartDate: weekStart, hardSync: true };
  if (locationId) body.locationId = locationId;
  const syncResponse = await client.post('/timecards/sync', body);
  if (syncResponse.status === 400 && syncResponse.data?.message?.includes('approved')) {
    return { shiftsRemoved: shifts.length, attendanceRemoved: attendance.length, syncSkipped: 'approved' };
  }
  await expectOk(syncResponse, 'POST /timecards/sync hardSync cleanup');

  return { shiftsRemoved: shifts.length, attendanceRemoved: attendance.length };
}

function resolveBreakUseGlobalSettings(shiftDef) {
  if (shiftDef.breakUseGlobalSettings != null) return !!shiftDef.breakUseGlobalSettings;
  // Explicit paid flag or unpaid hours means a shift override, not company global.
  if (shiftDef.breakTimePaid != null || shiftDef.unpaidHours != null) return false;
  return true;
}

async function createShift(client, shiftDef, defaults) {
  const date = shiftDef.date;
  const body = {
    employeeId: shiftDef.employeeId ?? defaults.employeeId,
    locationId: shiftDef.locationId ?? defaults.locationId,
    positionId: shiftDef.positionId ?? defaults.positionId,
    scheduleId: shiftDef.scheduleId ?? defaults.scheduleId,
    startTime: localDateTime(date, shiftDef.start),
    endTime: localDateTime(shiftDef.endDate || date, shiftDef.end),
    unpaidHours: shiftDef.unpaidHours ?? 0,
    breakTimePaid: shiftDef.breakTimePaid ?? false,
    breakUseGlobalSettings: resolveBreakUseGlobalSettings(shiftDef),
  };
  const response = await client.post('/shifts', body);
  const data = await expectOk(response, 'POST /shifts');
  return data;
}

async function createAttendance(client, attDef, defaults) {
  const body = {
    employeeId: attDef.employeeId ?? defaults.employeeId,
    locationId: attDef.locationId ?? defaults.locationId,
    clockIn: localDateTime(attDef.date, attDef.clockIn),
  };
  if (attDef.clockOut) {
    body.clockOut = localDateTime(attDef.clockOutDate || attDef.date, attDef.clockOut);
  }
  if (attDef.scheduleId != null) body.scheduleId = attDef.scheduleId;
  const response = await client.post('/attendance/manual', body);
  const data = await expectOk(response, 'POST /attendance/manual');
  return data;
}

async function hardSync(client, employeeId, weekStart, locationId) {
  const body = { employeeId, weekStartDate: weekStart, hardSync: true };
  if (locationId) body.locationId = locationId;
  const response = await client.post('/timecards/sync', body);
  await expectOk(response, 'POST /timecards/sync hardSync');
}

async function syncTimecard(client, employeeId, weekStart, locationId) {
  const body = { employeeId, weekStartDate: weekStart };
  if (locationId) body.locationId = locationId;
  const response = await client.post('/timecards/sync', body);
  return expectOk(response, 'POST /timecards/sync');
}

async function getSyncStep1(client, employeeId, weekStart, locationId) {
  const body = { employeeId, weekStartDate: weekStart };
  if (locationId) body.locationId = locationId;
  const response = await client.post('/timecards/sync-step1', body);
  return expectOk(response, 'POST /timecards/sync-step1');
}

async function addManualTimecardDay(client, timecardId, dayDef) {
  const response = await client.post(`/timecards/${timecardId}/day`, dayDef);
  return expectOk(response, 'POST /timecards/{id}/day');
}

async function seedScenarioData(client, seed, defaults) {
  const created = { shifts: [], attendance: [] };
  for (const shift of seed.shifts || []) {
    created.shifts.push(await createShift(client, shift, defaults));
  }
  for (const att of seed.attendance || []) {
    created.attendance.push(await createAttendance(client, att, defaults));
  }
  return created;
}

async function approveTimecard(client, timecardId) {
  return client.post(`/timecards/${timecardId}/approve`);
}

async function updateTimecardDay(client, timecardId, dayId, updates) {
  const response = await client.put(`/timecards/${timecardId}/day/${dayId}`, updates);
  return expectOk(response, `PUT /timecards/${timecardId}/day/${dayId}`);
}

async function hardSyncRequest(client, employeeId, weekStart, locationId) {
  const body = { employeeId, weekStartDate: weekStart, hardSync: true };
  if (locationId) body.locationId = locationId;
  return client.post('/timecards/sync', body);
}

async function getBreakSettings(client) {
  const response = await client.get('/break-time-settings');
  return expectOk(response, 'GET /break-time-settings');
}

async function saveBreakSettings(client, settings) {
  const body = {
    isPaid: settings.isPaid ?? false,
    unpaidPunchRequired: settings.unpaidPunchRequired ?? false,
    unpaidForceMinFromRules: settings.unpaidForceMinFromRules ?? true,
    unpaidUseShiftBreakAsFloor: settings.unpaidUseShiftBreakAsFloor ?? true,
    paidPunchRequired: settings.paidPunchRequired ?? false,
    rules: (settings.rules || []).map((r) => ({
      minHours: r.minHours,
      maxHours: r.maxHours,
      breakMinutes: r.breakMinutes,
    })),
  };
  const response = await client.post('/break-time-settings', body);
  return expectOk(response, 'POST /break-time-settings');
}

const CLOCK_SETTING_KEYS = [
  'timecard_clock_in_early',
  'timecard_clock_in_late',
  'timecard_clock_out_early',
  'timecard_clock_out_late',
];

async function getSystemSetting(client, key) {
  const response = await client.get(`/system-settings/${key}`);
  if (response.status === 404) return null;
  const data = await expectOk(response, `GET /system-settings/${key}`);
  return data?.value ?? null;
}

async function putSystemSetting(client, key, value) {
  const response = await client.put(`/system-settings/${key}`, { value });
  await expectOk(response, `PUT /system-settings/${key}`);
}

async function getClockSettings(client) {
  const out = {};
  for (const key of CLOCK_SETTING_KEYS) {
    out[key] = await getSystemSetting(client, key);
  }
  return out;
}

/** Overlay selected clock rules; returns prior values so the caller can restore. */
async function applyClockSettings(client, overlay) {
  const saved = {};
  for (const [key, value] of Object.entries(overlay || {})) {
    if (value == null || value === '') continue;
    saved[key] = await getSystemSetting(client, key);
    await putSystemSetting(client, key, String(value));
  }
  return saved;
}

async function restoreClockSettings(client, saved) {
  for (const [key, value] of Object.entries(saved || {})) {
    if (value == null || value === '') continue;
    await putSystemSetting(client, key, String(value));
  }
}

async function ensureEmployeeSubLocations(client, employeeId, subLocationIds, defaultAppRoleId = 3) {
  const emp = await expectOk(await client.get(`/employees/${employeeId}`), `GET /employees/${employeeId}`);
  const existingSubs = emp.profile?.subLocationIds || [];
  const mergedSubs = [...new Set([...existingSubs, ...subLocationIds])];
  const existingRoles = emp.profile?.appRoleIds || [];

  const body = {
    updateSubLocations: true,
    subLocationIds: mergedSubs,
  };

  if (existingRoles.length === 0) {
    body.updateAppRoles = true;
    body.appRoleIds = [defaultAppRoleId];
  }

  const response = await client.patch(`/employees/${employeeId}`, body);
  await expectOk(response, `PATCH /employees/${employeeId} subLocations`);
}

async function ensureEmployeeAppRole(client, employeeId, roleId) {
  const emp = await expectOk(await client.get(`/employees/${employeeId}`), `GET /employees/${employeeId}`);
  const existing = emp.profile?.appRoleIds || [];
  if (existing.length > 0) {
    return { added: false, appRoleIds: existing };
  }
  const response = await client.patch(`/employees/${employeeId}`, {
    updateAppRoles: true,
    appRoleIds: [roleId],
  });
  await expectOk(response, `PATCH /employees/${employeeId} appRole`);
  return { added: true, appRoleIds: [roleId] };
}

async function ensureEmployeePositions(client, employeeId, positionIds) {
  const emp = await expectOk(await client.get(`/employees/${employeeId}`), `GET /employees/${employeeId}`);
  const main = emp.profile?.mainPositionId;
  const existing = emp.profile?.subPositionIds || [];
  const needed = (positionIds || []).filter((id) => id != null);
  const missing = needed.filter((id) => id !== main && !existing.includes(id));
  if (missing.length === 0) {
    return { added: false };
  }
  const merged = [...new Set([...existing, ...missing])];
  const response = await client.patch(`/employees/${employeeId}`, {
    updateSubPositions: true,
    subPositionIds: merged,
  });
  await expectOk(response, `PATCH /employees/${employeeId} positions`);
  return { added: true };
}

async function ensureEmployeeSchedules(client, employeeId, scheduleIds) {
  const emp = await expectOk(await client.get(`/employees/${employeeId}`), `GET /employees/${employeeId}`);
  const existing = emp.profile?.scheduleIds || [];
  const needed = (scheduleIds || []).filter((id) => id != null);
  const missing = needed.filter((id) => !existing.includes(id));
  if (missing.length === 0) {
    return { added: false, scheduleIds: existing };
  }

  const merged = [...new Set([...existing, ...needed])];
  const response = await client.patch(`/employees/${employeeId}`, {
    updateSchedules: true,
    scheduleIds: merged,
  });
  await expectOk(response, `PATCH /employees/${employeeId} schedules`);
  return { added: true, scheduleIds: merged };
}

function findDayByCriteria(days, criteria) {
  return days.find((day) => {
    const dateKey = String(day.date).includes('T') ? day.date.split('T')[0] : String(day.date).slice(0, 10);
    if (criteria.date && dateKey !== criteria.date) return false;
    if (criteria.mode && day.mode !== criteria.mode) return false;
    if (criteria.hasActuals === true && (!day.actualIn || !day.actualOut)) return false;
    if (criteria.hasActuals === false && (day.actualIn || day.actualOut)) return false;
    if (criteria.noSchedule === true && (day.scheduledIn || day.scheduledOut)) return false;
    return true;
  });
}

export {
  cleanupWeek,
  createShift,
  createAttendance,
  hardSync,
  hardSyncRequest,
  syncTimecard,
  getTimecard,
  getSyncStep1,
  addManualTimecardDay,
  updateTimecardDay,
  seedScenarioData,
  fetchShifts,
  fetchAttendance,
  approveTimecard,
  unapproveTimecard,
  findDayByCriteria,
  deleteAttendance,
  ensureEmployeeSubLocations,
  ensureEmployeeSchedules,
  ensureEmployeeAppRole,
  ensureEmployeePositions,
  getBreakSettings,
  saveBreakSettings,
  getClockSettings,
  applyClockSettings,
  restoreClockSettings,
};
